import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDom from 'react-dom';
import { useDispatch, connect } from 'react-redux';
import { Button } from 'carbon-components-react';
import { Search16 } from '@carbon/icons-react';
import PropTypes from 'prop-types';
import ResultsBox from './resultsBox';
import {
  MAP_DATA_SERVICE_DATA_SET,
  CFCProps,
  MapSearchLocationHistoryObject,
  MapPlaceSuggestion,
  MapPlaceSuggestionCustomFields
} from 'types';
import { setMapSearchLocationHistory } from 'redux/app/actions';
import {
  clearPlaceSuggest,
  getPlaceCandidateAddressStart,
  getGeoscapeAddressStart,
  getGeoscapeLandParcelStart,
  clearGeoscapeAddress,
  clearGeoscapeLandParcel,
  clearPlaceCandidateAddress
} from 'redux/map/actions';
import { addAlert } from 'redux/alerts/actions';
import { RootState } from 'redux/root.reducer';
import { useEventListener, useComponentMount } from 'utilities/hooks.util';
import {
  optimizeLabel,
  isNeedPin,
  constructArcGISWorldGeocodingLocality
} from './helper';
import { removePin, addPin, zoomToCenter } from 'components/Map/helper';
import Settings from 'components/Map/settings';
import SearchTypeSelection, { scopes } from './searchTypeSelection';
import AddressSearch from './addressSearch';
import LatLongSearch from './latLongSearch';
import LotNumberSearch from './lotNumberSearch';
import ParcelPopup from './parcelPopup';

const MapLocationSearch: React.FC<Props> = ({
  noOpenButton,
  onOpen,
  open,
  buttonDisabled,
  buttonClassName,
  suggestion,
  error,
  user,
  mapSearchLocationHistory,
  geoscapeAddress,
  landParcel,
  onCancelLandParcel,
  onUseLandParcel,
  addressCandidate
}) => {
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchMapZoomLevel = Settings.zoomLevel;
  const [isOpen, setIsOpen] = useState<boolean>(noOpenButton || false);
  const [isShowResult, setIsShowResult] = useState<boolean>(false);
  const [
    userMapLocationSearchHistory,
    seUserMapLocationSearchHistory
  ] = useState<MapSearchLocationHistoryObject[]>([]);
  const [currentScope, setCurrentScope] = useState<string>(
    MAP_DATA_SERVICE_DATA_SET.GNAF
  );
  const dispatch = useDispatch();
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  const saveHistory = useCallback(
    (scope: string, item: MapPlaceSuggestion) => {
      const searchHistories = mapSearchLocationHistory || {};
      const { Value } = item;

      const searchHistoryOtherScope = userMapLocationSearchHistory.filter(
        (sh: any) => sh.scope !== scope
      );
      let newSearchHistorySameScope = userMapLocationSearchHistory.filter(
        (sh: any) => sh.Value !== Value && sh.scope === scope
      );
      const newHistory = { scope, ...item };
      newSearchHistorySameScope = [newHistory, ...newSearchHistorySameScope];
      if (newSearchHistorySameScope.length > 5) {
        newSearchHistorySameScope.pop();
      }
      const newSearchHistory = [
        ...newSearchHistorySameScope,
        ...searchHistoryOtherScope
      ];
      searchHistories[user.id] = newSearchHistory;
      seUserMapLocationSearchHistory(newSearchHistory);
      dispatch(setMapSearchLocationHistory(searchHistories));
    },
    [userMapLocationSearchHistory, mapSearchLocationHistory, user.id, dispatch]
  );

  const clickOnUseParcel = useCallback(() => {
    if (
      typeof onUseLandParcel === 'function' &&
      geoscapeAddress?.selectedSuggestion
    ) {
      dispatch(
        getGeoscapeLandParcelStart({
          selectedSuggestion: geoscapeAddress.selectedSuggestion
        })
      );
      const { loadedView } = window;
      loadedView && loadedView.closePopup();
    }
  }, [onUseLandParcel, geoscapeAddress, dispatch]);

  const parcelPopupTemplate = {
    content: useCallback(() => {
      const node = document.createElement('div');
      ReactDom.render(
        <ParcelPopup
          address={geoscapeAddress?.address}
          onButtonClick={() => clickOnUseParcel()}
          onLinkClick={
            typeof onCancelLandParcel === 'function'
              ? () => onCancelLandParcel()
              : undefined
          }
        />,
        node
      );
      return node;
    }, [geoscapeAddress, onCancelLandParcel, clickOnUseParcel])
  };

  const searchCoordinate = (
    value: string,
    center: any[],
    coordinate: string
  ) => {
    zoomToCenter(center);
    user?.id &&
      saveHistory(currentScope, {
        Value: value,
        Coordinate: coordinate,
        Highlight: '',
        Score: '',
        CustomFields: { locality: '', region: '' }
      });
    closeSearch();
  };

  const onClickOpenButton = () => {
    if (typeof open !== 'boolean') {
      setIsOpen(!isOpen);
    }
    if (typeof onOpen === 'function') {
      onOpen();
    }
  };

  const processAddressCandidate = useCallback(() => {
    const addrCandidate = addressCandidate?.[0];

    if (addrCandidate) {
      const addrCandidateAttr = addrCandidate.attributes;
      const CustomFields: MapPlaceSuggestionCustomFields = {
        locality:
          addrCandidateAttr.Addr_type === 'Locality'
            ? `${constructArcGISWorldGeocodingLocality(addrCandidateAttr)}`
            : '',
        region: addrCandidate.attributes.Region || ''
      };

      user?.id &&
        saveHistory(currentScope, {
          Coordinate: `${addrCandidate.location.x}, ${addrCandidate.location.y}`,
          CustomFields,
          Highlight: addrCandidate.address,
          Score: addrCandidate.score.toString(),
          Value: addrCandidate.address,
          Extent: addrCandidate.extent
        });
      if (window.loadedView) {
        removePin();
        const center = [addrCandidate.location.x, addrCandidate.location.y];
        if (isNeedPin(currentScope, addrCandidate.address, CustomFields)) {
          addPin(center);
        }
        // Somehow using arcgis world geocoding coordinate does not give same zoom level with mds.
        // ArcGIS world geocoding zoom level need to offset by 1
        window.loadedView
          .goTo({ center, zoom: searchMapZoomLevel + 1 })
          .then(() => {
            // Save the extent after zooming so it persists across page navigation
            if (window.loadedView && !window.loadedView.destroyed) {
              window.mapOriginalExtent = window.loadedView.extent;
            }
          });
      }
      setSearchInputValue(
        optimizeLabel(
          currentScope,
          addrCandidate.address,
          CustomFields,
          true,
          true
        )
      );
    } else {
      dispatch(
        addAlert(
          'map-location-search-alert',
          'error',
          'Error',
          'Invalid map search data'
        )
      );
    }

    dispatch(clearPlaceCandidateAddress());
  }, [
    addressCandidate,
    user,
    currentScope,
    saveHistory,
    searchMapZoomLevel,
    dispatch
  ]);

  const processGeoscapeAddress = useCallback(() => {
    if (
      geoscapeAddress &&
      geoscapeAddress.coordinate &&
      geoscapeAddress.selectedSuggestion
    ) {
      user?.id && saveHistory(currentScope, geoscapeAddress.selectedSuggestion);
      if (window.loadedView) {
        removePin();
        const center = geoscapeAddress.coordinate
          .split(',')
          ?.map((c: string) => Number(c));
        if (
          isNeedPin(
            currentScope,
            geoscapeAddress.selectedSuggestion.Value,
            geoscapeAddress.selectedSuggestion.CustomFields
          )
        ) {
          const popupTemplate =
            typeof onUseLandParcel === 'function'
              ? parcelPopupTemplate
              : undefined;
          addPin(center, popupTemplate);
        }
        window.loadedView
          .goTo({
            center,
            zoom: searchMapZoomLevel + 1
          })
          .then(() => {
            // Save the extent after zooming so it persists across page navigation
            if (window.loadedView && !window.loadedView.destroyed) {
              window.mapOriginalExtent = window.loadedView.extent;
            }
          });
      }
      setSearchInputValue(
        optimizeLabel(
          currentScope,
          geoscapeAddress.selectedSuggestion.Value,
          geoscapeAddress.selectedSuggestion.CustomFields,
          false,
          false
        )
      );
    } else {
      dispatch(
        addAlert(
          'map-location-search-alert',
          'error',
          'Error',
          'Invalid map search data'
        )
      );
    }

    dispatch(clearGeoscapeAddress());
  }, [
    geoscapeAddress,
    user,
    saveHistory,
    currentScope,
    searchMapZoomLevel,
    dispatch,
    onUseLandParcel,
    parcelPopupTemplate
  ]);

  useEffect(() => {
    if (typeof open === 'boolean' && !noOpenButton) {
      setIsOpen(open);
    }
  }, [open, noOpenButton]);

  useEffect(() => {
    if (error) {
      dispatch(addAlert('map-location-search-alert', 'error', 'Error', error));
    }

    return () => {
      dispatch(clearPlaceSuggest());
    };
  }, [error, dispatch]);

  useEffect(() => {
    searchButtonRef?.current?.blur();
  }, [isOpen]);

  useEffect(() => {
    user?.id &&
      mapSearchLocationHistory?.[user.id] &&
      seUserMapLocationSearchHistory(mapSearchLocationHistory[user.id]);
  }, [user, mapSearchLocationHistory]);

  useEffect(() => {
    if (addressCandidate) {
      processAddressCandidate();
    }
  }, [addressCandidate, processAddressCandidate]);

  useEffect(() => {
    if (geoscapeAddress) {
      processGeoscapeAddress();
    }
  }, [geoscapeAddress, processGeoscapeAddress]);

  useEffect(() => {
    if (typeof onUseLandParcel === 'function' && landParcel) {
      onUseLandParcel(landParcel);
      dispatch(clearGeoscapeLandParcel());
    }
  }, [landParcel, onUseLandParcel, dispatch]);

  const onClickResult = (item: any) => {
    if (item.AddressId) {
      dispatch(
        getGeoscapeAddressStart({
          selectedSuggestion: item
        })
      );
    } else if (item.Value && item.Coordinate && item.CustomFields) {
      user?.id && saveHistory(currentScope, item);
      if (window.loadedView) {
        removePin();
        const center = item.Coordinate.split(',')?.map((c: string) =>
          Number(c)
        );
        if (isNeedPin(currentScope, item.Value, item.CustomFields)) {
          addPin(center);
        }
        window.loadedView
          .goTo({
            center,
            zoom: item.Extent ? searchMapZoomLevel + 1 : searchMapZoomLevel
          })
          .then(() => {
            // Save the extent after zooming so it persists across page navigation
            if (window.loadedView && !window.loadedView.destroyed) {
              window.mapOriginalExtent = window.loadedView.extent;
            }
          });
      }
      setSearchInputValue(
        optimizeLabel(
          currentScope,
          item.Value,
          item.CustomFields,
          !!item.Extent,
          !!item.Extent
        )
      );
    } else if (item.MagicKey && item.Value) {
      // This is for list coming from ArcGIS World Geocoding
      dispatch(
        getPlaceCandidateAddressStart({
          query: item.Value,
          outFields: '*',
          magicKey: item.MagicKey
        })
      );
    } else {
      dispatch(
        addAlert(
          'map-location-search-alert',
          'error',
          'Error',
          'Invalid map search data'
        )
      );
    }

    if (
      noOpenButton &&
      item.isHistory &&
      item.scope === MAP_DATA_SERVICE_DATA_SET.GNAFLPID
    ) {
      setSearchInputValue('');
    }

    showHideBox(false, false);
    closeSearch();
  };

  const getPlaceHolder = () => {
    const { loadedView } = window;
    const scope = scopes.find((s) => s.value === currentScope);
    return currentScope === 'gps' && loadedView
      ? `${loadedView.center.latitude.toFixed(
          4
        )},${loadedView.center.longitude.toFixed(4)}`
      : scope?.placeholderLabel || '';
  };

  const showHideBox = (scope: boolean, result: boolean) => {
    setIsShowResult(result);
  };

  const selectScope = (item: any) => {
    setCurrentScope(item?.value);
    showHideBox(false, false);
    setSearchInputValue('');
    dispatch(clearPlaceSuggest());
  };

  const closeSearch = () => {
    if (!noOpenButton) {
      setIsOpen(false);
      setSearchInputValue('');
    }
  };

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          showHideBox(false, false);
        }
      }
    },
    [containerRef]
  );

  const onClickCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const center = [position.coords.longitude, position.coords.latitude];
        setSearchInputValue('');
        zoomToCenter(center);
      },
      (err) => {
        const title = 'Error';
        const msg = err.message || err.toString();
        dispatch(addAlert('map-location-search-alert', 'error', title, msg));
      }
    );
    showHideBox(false, false);
    closeSearch();
  };

  const renderSearchInput = (type: string) => {
    switch (type) {
      case MAP_DATA_SERVICE_DATA_SET.GNAF:
        return (
          <AddressSearch
            showHideBox={() => showHideBox(false, true)}
            placeholder={getPlaceHolder()}
            noOpenButton={noOpenButton}
            currentScope={currentScope}
            searchResult={searchInputValue}
          />
        );
      case MAP_DATA_SERVICE_DATA_SET.GNAFLPID:
        return (
          <LotNumberSearch
            showHideBox={() => showHideBox(false, true)}
            placeholder={getPlaceHolder()}
            noOpenButton={noOpenButton}
            currentScope={currentScope}
            searchResult={searchInputValue}
          />
        );
      case 'gps':
        return (
          <LatLongSearch
            showHideBox={() => showHideBox(false, true)}
            placeholder={getPlaceHolder()}
            noOpenButton={noOpenButton}
            searchResult={searchInputValue}
            onClickSearchCoordinate={searchCoordinate}
          />
        );
    }
  };

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  useComponentMount(() => {
    return () => {
      removePin();
    };
  });

  return (
    <div
      ref={containerRef}
      className={`mls-v2-container${isShowResult ? ' is-show-result' : ''}`}
    >
      {!noOpenButton && (
        <div
          className={`mls-button-container${isOpen ? ' is-open' : ''}${
            isShowResult ? ' is-show-result' : ''
          }`}
        >
          <Button
            renderIcon={Search16}
            iconDescription="Search"
            tooltipPosition="right"
            hasIconOnly
            kind="tertiary"
            size="small"
            disabled={buttonDisabled}
            className={buttonClassName}
            onClick={onClickOpenButton}
            ref={searchButtonRef}
          />
        </div>
      )}
      {(noOpenButton || isOpen) && (
        <div className="mls-control-wrapper">
          {renderSearchInput(currentScope)}
          <SearchTypeSelection
            type={currentScope}
            onChange={(val: any) => {
              selectScope(val);
            }}
          ></SearchTypeSelection>
          {isShowResult && (
            <div className="mls-item-container">
              {isShowResult && (
                <ResultsBox
                  scope={currentScope}
                  userSearchHistory={userMapLocationSearchHistory}
                  items={suggestion}
                  onItemClick={onClickResult}
                  onClickCurrentLocation={onClickCurrentLocation}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const propTypes = {
  noOpenButton: PropTypes.bool,
  onOpen: PropTypes.func,
  onUseLandParcel: PropTypes.func,
  onCancelLandParcel: PropTypes.func,
  open: PropTypes.bool,
  buttonDisabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  selectSearchTooltipPosition: PropTypes.string
};

type ownProps = PropTypes.InferProps<typeof propTypes>;

MapLocationSearch.defaultProps = {
  noOpenButton: false,
  buttonDisabled: false,
  buttonClassName: ''
};

const mapStateToProps = ({ app, mapCache, session }: RootState) => ({
  mapSearchLocationHistory: app.mapSearchLocationHistory,
  user: session.currentUser,
  suggestion: mapCache.suggestion,
  error: mapCache.suggestionError,
  geoscapeAddress: mapCache.geoscapeAddress,
  landParcel: mapCache.landParcel,
  addressCandidate: mapCache.addressCandidate
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

export default connector(MapLocationSearch);
